/**
 * OCR Connector configuration component.
 * Specialized configuration for OCR connector (Google Cloud Vision / Azure Document Intelligence).
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  TextField,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';

export interface OcrConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * OCR Connector configuration component.
 */
export const OcrConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: OcrConnectorConfigProps) => {
  const [provider, setProvider] = useState<'google' | 'azure'>('google');
  const [apiKey, setApiKey] = useState('');
  const [projectId, setProjectId] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [baseUrl, setBaseUrl] = useState('');
  const [defaultModel, setDefaultModel] = useState('');

  const handleProviderChange = (newProvider: 'google' | 'azure') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && provider === 'google') {
      // For Google, we'd typically upload the service account JSON
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const settings = JSON.parse(settingsJson || '{}');
        settings.serviceAccountJson = content;
        onChange?.(JSON.stringify(settings));
      };
      reader.readAsText(file);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          OCR Provider Configuration
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select Provider
          </Typography>
          <ToggleButtonGroup
            value={provider}
            exclusive
            onChange={(_, newProvider) => newProvider && handleProviderChange(newProvider)}
            fullWidth
          >
            <ToggleButton value="google">Google Cloud Vision</ToggleButton>
            <ToggleButton value="azure">Azure Document Intelligence</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {provider === 'google' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Service Account JSON Key
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Upload Service Account JSON
                <input
                  type="file"
                  hidden
                  accept=".json"
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>
            <TextField
              label="API Key (Alternative)"
              type="password"
              value={apiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setApiKey(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.apiKey = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Alternative: Use API key instead of service account"
            />
            <TextField
              label="Project ID"
              value={projectId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setProjectId(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.projectId = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Google Cloud project ID"
            />
            <TextField
              label="Default Language"
              value={defaultLanguage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDefaultLanguage(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultLanguage = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default language code (e.g., en, es, fr)"
            />
          </Box>
        )}

        {provider === 'azure' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="API Endpoint"
              value={baseUrl}
              onChange={(e) => {
                setBaseUrl(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.endpoint = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Azure Document Intelligence endpoint URL"
            />
            <TextField
              label="API Key"
              type="password"
              value={apiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setApiKey(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.apiKey = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
            />
            <TextField
              label="Default Model ID"
              value={defaultModel}
              onChange={(e) => {
                setDefaultModel(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultModelId = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Pre-built model ID (e.g., prebuilt-document, prebuilt-invoice)"
            />
            <TextField
              label="Default Language"
              value={defaultLanguage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDefaultLanguage(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultLanguage = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
